import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  Input,
  OnChanges,
  signal,
  SimpleChanges,
  ViewChild,
  afterNextRender,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SvgIconRegistryService } from '@hglobal/common';
import { AuthService } from '@fb/services/auth/auth.service';
import {
  AiChatContext,
  FloatingButtonConfig,
  FloatingButtonEnvironmentOverrides,
  FloatingButtonUser,
} from '@fb/models';
import { TranslationPathService } from '@fb/services/translation/translation-path.service';
import { DictionariesService } from '@fb/services/dictionaries.service';
import { applyFloatingButtonEnvironmentOverrides } from '@fb/helpers/apply-floating-button-environment-overrides';
import { buildFloatingButtonIconSet } from '@fb/helpers/build-floating-button-icon-set';
import { parseFloatingButtonConfig } from '@fb/helpers/parse-floating-button-config';
import { parseFloatingButtonEnvironmentOverrides } from '@fb/helpers/parse-floating-button-environment-overrides';

import { AiChatFormComponent } from '../ai-chat-form/ai-chat-form.component';
import { AiChatFormService } from '../ai-chat-form/ai-chat-form.service';
import { AI_CHAT_DEFAULT_CONTEXT } from '../ai-chat-form/ai-chat.constants';
import {
  AiChatTabBridgeService,
  readAiChatContextFromBrowserPath,
} from '../ai-chat-form/ai-chat-tab-bridge.service';

@Component({
  selector: 'ai-chat-page',
  templateUrl: './ai-chat-page.component.html',
  styleUrl: './ai-chat-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    AiChatFormComponent,
  ],
  providers: [
    AiChatFormService,
    AiChatTabBridgeService,
  ],
})
export class AiChatPageComponent implements OnChanges, AfterViewInit {
  @Input() user?: FloatingButtonUser | string | null;

  @Input() translationPath?: string;
  @Input() language?: string = 'en';
  @Input() config: FloatingButtonConfig;
  @Input() environmentOverrides?: FloatingButtonEnvironmentOverrides | string;

  /** Host tab (`FOR_ME` / `FOR_WORK`). */
  @Input({ alias: 'active-chat-tab' }) activeChatTab?: string | null;

  @ViewChild('shellContainer') shellContainerElementRef: ElementRef;

  readonly #authService = inject(AuthService);
  readonly #injector = inject(Injector);
  readonly #destroyRef = inject(DestroyRef);
  readonly #tabBridge = inject(AiChatTabBridgeService);
  readonly #translateService = inject(TranslateService);
  readonly #translationPathService = inject(TranslationPathService);
  readonly #dictionariesService = inject(DictionariesService);
  readonly #iconsService = inject(SvgIconRegistryService);

  readonly chatFormReady = signal(false);

  constructor() {
    afterNextRender(
      () => {
        this.#syncUserFromInputs();
        this.#syncActiveTabFromHostShell();
        this.#tabBridge
          .loadTabsAndLicenses()
          .pipe(takeUntilDestroyed(this.#destroyRef))
          .subscribe({
            next: () => this.#finishTabsBootstrap(),
            error: () => this.#finishTabsBootstrap(),
          });
      },
      { injector: this.#injector },
    );
  }

  ngOnChanges({
    language,
    translationPath,
    user,
    config,
    environmentOverrides,
    activeChatTab,
  }: SimpleChanges): void {
    if (environmentOverrides) {
      applyFloatingButtonEnvironmentOverrides(
        parseFloatingButtonEnvironmentOverrides(environmentOverrides.currentValue),
      );
    }

    if (language || translationPath) {
      const lang = language?.currentValue || this.language;
      this.#translateService.use(lang);

      this.#translationPathService.setTranslationPath(
        translationPath?.currentValue || this.translationPath,
      );
    }

    if (user !== undefined) {
      this.#syncUserFromInputs();
    }

    if (config?.currentValue) {
      this.#applyConfig(config.currentValue);
    }

    if (activeChatTab !== undefined && this.chatFormReady()) {
      this.#syncActiveTabFromHostShell();
    }
  }

  ngAfterViewInit(): void {
    this.#registerIconsIfReady();
  }

  #registerIconsIfReady(): void {
    const host = this.shellContainerElementRef?.nativeElement;

    if (!host) {
      return;
    }

    this.#iconsService.addIcons(buildFloatingButtonIconSet(this.config), host);
  }

  #applyConfig(config: FloatingButtonConfig | string): void {
    const parsedConfig = parseFloatingButtonConfig(config);

    this.#dictionariesService.addTranslations(
      parsedConfig.translations,
      this.language || 'en',
    );

    this.#registerIconsIfReady();
  }

  #syncUserFromInputs(): void {
    this.#authService.setUser(this.user);
  }

  #syncActiveTabFromHostShell(): void {
    const fromPath = readAiChatContextFromBrowserPath();
    const fromInput = this.#contextFromHostInput(this.activeChatTab);

    this.#tabBridge.syncFromHost(fromPath ?? fromInput ?? AI_CHAT_DEFAULT_CONTEXT);
  }

  /** Host still sends `'FOR_ME'` / `'FOR_WORK'`; also accept `'PERSONAL'` / `'WORK'`. */
  #contextFromHostInput(input: string | null | undefined): AiChatContext | null {
    if (input === AiChatContext.Work || input === 'FOR_WORK') {
      return AiChatContext.Work;
    }

    if (input === AiChatContext.Personal || input === 'FOR_ME') {
      return AiChatContext.Personal;
    }

    return null;
  }

  #finishTabsBootstrap(): void {
    queueMicrotask(() => {
      this.#syncActiveTabFromHostShell();
      this.chatFormReady.set(true);
    });
  }
}
